import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RecordContextProvider, useGetIdentity, useGetOne, useNotify } from 'react-admin';
import { ArrowLeft, Check, Clock, Link2, Loader2, Megaphone, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CustomerEvent, Funeral } from '../types';
import { adLabel, asCallContext, callContextText } from './callContext';
import { ContactActivityFeed } from '../components/ContactActivityFeed';
import { MessageInputBar } from '../funerals/components/MessageInputBar';
import { MessageDraftProvider } from '../funerals/contexts/MessageDraftContext';
import { FuneralShowFeed } from '../funerals/FuneralShowFeed';
import { useFuneralClients } from '../funerals/hooks/useFuneralClients';
import { getClientColor } from '../funerals/components/ClientFilterBar';
import { useScrollToBottom } from '../hooks/useScrollToBottom';
import {
  InboxItem,
  RecordTarget,
  contactLabel,
  conversationPhone,
  recordTarget,
  sameRecord,
  stageLabel,
  stageTone,
} from './types';
import { laterToday, markDone, snooze, tomorrowMorning } from './inboxApi';
import { OwnerSelector } from '../components/OwnerSelector';
import { LeadOwnerControls } from '../leads/LeadShowToolBar';
import { CallLinkProvider } from '../caller-links/CallLinkContext';
import { LinkCallerDialog } from '../caller-links/LinkCallerDialog';

// ─── A conversation that belongs to a case ───────────────────────────────────

function FuneralConversation({
  funeralId,
  openEmailId,
}: {
  funeralId: number;
  openEmailId?: string;
}) {
  const { data: funeral, isLoading } = useGetOne<Funeral>('funerals', { id: funeralId });
  const { coloredClients, clientByRecipientId, clientByContactDetail, clientColorMap } =
    useFuneralClients(funeral);

  const [activeClientIds, setActiveClientIds] = useState<Set<number>>(new Set());
  useEffect(() => {
    if (coloredClients.length > 0) {
      setActiveClientIds(new Set(coloredClients.map((c) => c.id)));
    }
  }, [coloredClients]);

  if (isLoading || !funeral) {
    return (
      <div className="flex flex-1 items-center justify-center text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  // `showSystemEvents={false}` is what makes this the communications view of a
  // case rather than the whole case: SMS, calls and notes, no status changes.
  // Partners are emailed from the case page, so the inbox composer skips them.
  return (
    <MessageDraftProvider>
      <RecordContextProvider value={funeral}>
        <div className="flex min-h-0 flex-1 flex-col">
          <FuneralShowFeed
            coloredClients={coloredClients}
            activeClientIds={activeClientIds}
            clientByRecipientId={clientByRecipientId}
            clientByContactDetail={clientByContactDetail}
            clientColorMap={clientColorMap}
            showSystemEvents={false}
            showPartners={false}
            openEmailId={openEmailId}
          />
        </div>
      </RecordContextProvider>
    </MessageDraftProvider>
  );
}

// ─── A conversation with a lead, or a person with no case at all ─────────────

function ContactConversation({ item, openEmailId }: { item: InboxItem; openEmailId?: string }) {
  const [optimistic, setOptimistic] = useState<CustomerEvent[]>([]);
  // Lead and client threads had no auto-scroll at all, so they opened at the
  // oldest message and you had to scroll down to find what you were answering.
  const { endRef, scrollToBottom } = useScrollToBottom(item.conversation_key);

  const clients = useMemo(
    () =>
      item.client_id
        ? [
            {
              id: item.client_id,
              name: item.contact_name || item.contact_phone || 'Unknown',
              color: getClientColor(0),
              phone_number: item.contact_phone || '',
              email: item.contact_email,
            },
          ]
        : [],
    [item.client_id, item.contact_name, item.contact_phone, item.contact_email],
  );

  const handleSent = useCallback(
    (event: CustomerEvent) => {
      setOptimistic((prev) => [...prev, event]);
      // Follow your own message down.
      window.setTimeout(scrollToBottom, 50);
    },
    [scrollToBottom],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ScrollArea className="min-h-0 flex-1">
        <CallLinkProvider>
          <ContactActivityFeed
            clientIds={item.client_id ? [item.client_id] : []}
            leadId={item.lead_id ?? undefined}
            optimisticEvents={optimistic}
            openEmailId={openEmailId}
          />
        </CallLinkProvider>
        <div ref={endRef} />
      </ScrollArea>
      <MessageInputBar
        clients={clients}
        defaultRecipientId={item.client_id ?? null}
        leadId={item.lead_id ?? undefined}
        onMessageSent={handleSent}
      />
    </div>
  );
}

// ─── An email from someone nobody has placed ─────────────────────────────────

/**
 * Just their emails, by address. There is no person to text and no case to file
 * on, so no composer: the reply is in the email viewer, and "Link to…" in the
 * header is how they stop being unmatched.
 */
function UnmatchedConversation({ item, openEmailId }: { item: InboxItem; openEmailId?: string }) {
  const { endRef } = useScrollToBottom(item.conversation_key);
  const address = item.conversation_key.replace(/^email:/, '');
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ScrollArea className="min-h-0 flex-1">
        <ContactActivityFeed clientIds={[]} emailAddress={address} openEmailId={openEmailId} />
        <div ref={endRef} />
      </ScrollArea>
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<string, string> = {
  quebec: 'QC',
  ontario: 'ON',
};

function ThreadHeader({
  item,
  onBack,
  openRecord,
  onToggleRecord,
  onChanged,
}: {
  item: InboxItem;
  onBack: () => void;
  openRecord: RecordTarget | null;
  onToggleRecord: (target: RecordTarget) => void;
  onChanged: () => void;
}) {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const [linkOpen, setLinkOpen] = useState(false);

  const act = useCallback(
    async (fn: () => Promise<void>, message: string) => {
      try {
        await fn();
        notify(message, { type: 'info' });
        onChanged();
      } catch (err: any) {
        notify(err?.message || 'That did not work', { type: 'warning' });
      }
    },
    [notify, onChanged],
  );

  const actor = { id: identity?.id as string | undefined, name: identity?.fullName };
  const phone = conversationPhone(item);
  const target = recordTarget(item);
  // The latest call came from an ad (T-058): say so, with the ad session the
  // call matched — keyword, landing page, campaign — so the callback can open
  // with what they were looking at. A menu hang-up gets its one-line context.
  const callContext =
    item.last_message_channel === 'sms' || item.last_message_channel === 'email'
      ? null
      : asCallContext(item.last_call_context);
  const ad = adLabel(callContext, true);
  const hangup = callContext?.ivr_hangup
    ? callContextText(callContext, {
        direction: item.last_message_direction,
        status: item.last_call_status,
        durationSeconds: item.last_call_duration_seconds,
      }, { full: true })
    : null;

  return (
    <div className="shrink-0 border-b">
      <div className="flex items-center gap-1.5 px-2 py-2">
        <button
          type="button"
          onClick={onBack}
          title="Back to the list"
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="truncate text-sm font-semibold">
          {contactLabel(item).label}
        </span>
        {item.language && (
          <span className="shrink-0 rounded border px-1 text-[9px] font-semibold uppercase text-muted-foreground">
            {item.language}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {/*
            A `tel:` link rather than a call to our own Aircall endpoint. The
            Aircall desktop app registers as the handler for tel:, so this dials
            from whoever is signed in on this machine — the existing in-app call
            button posts to the API with a hardcoded Aircall user id, which
            always rings that one person no matter who pressed it. Wrong for a
            shared inbox.

            Nothing is logged here either: the call comes back through the
            Aircall webhook and lands in this thread on its own, so writing an
            event would show it twice.
          */}
          {phone && (
            <a
              href={`tel:${phone}`}
              title={`Call ${phone}`}
              aria-label={`Call ${item.contact_name || phone}`}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Phone className="h-4 w-4" />
            </a>
          )}
          {/* T-189: a caller with a lead but no case may be family on one we have. */}
          {((item.lead_id && item.funeral_id == null) || item.is_unmatched) && (
            <button
              type="button"
              onClick={() => setLinkOpen(true)}
              title="Link to a case or lead…"
              aria-label="Link to a case or lead"
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Link2 className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() =>
              void act(() => markDone(item.conversation_key, actor), 'Marked done')
            }
            title="Mark done"
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Check className="h-4 w-4" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                title="Snooze"
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Clock className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="cleo-ui inbox-menu">
              <DropdownMenuItem
                onClick={() =>
                  void act(
                    () => snooze(item.conversation_key, laterToday(), actor),
                    'Snoozed until later today',
                  )
                }
              >
                Later today
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  void act(
                    () => snooze(item.conversation_key, tomorrowMorning(), actor),
                    'Snoozed until tomorrow 9am',
                  )
                }
              >
                Tomorrow morning
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Context strip: enough of the case to answer without opening it. The
          chip leads, in the list's customer/lead colour, and is how the case
          or lead is opened — and closed again. */}
      <div className="flex min-w-0 items-center gap-1.5 border-t bg-muted/40 py-1.5 pl-3 pr-2">
        {target ? (
          <RecordChip
            item={item}
            target={target}
            open={sameRecord(openRecord, target)}
            onToggle={() => onToggleRecord(target)}
          />
        ) : (
          <span className="text-xs text-muted-foreground">
            {item.is_unmatched ? 'Unmatched: link this sender to a case or lead' : 'No case or lead'}
          </span>
        )}
        {item.funeral_id != null && (
          <>
            <span
              className={cn(
                'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none',
                stageTone(item.cremation_status),
              )}
            >
              {stageLabel(item.cremation_status)}
            </span>
            {item.region && (
              <span className="shrink-0 rounded-full border bg-background px-1.5 py-0.5 text-[10px] leading-none text-muted-foreground">
                {STATUS_LABEL[item.region] ?? item.region}
              </span>
            )}
          </>
        )}
        {target && (
          <span className="ml-auto flex shrink-0 items-center gap-1">
            <ConversationOwner
              resource={target.kind === 'funeral' ? 'funerals' : 'leads'}
              id={target.id}
            />
          </span>
        )}
      </div>

      <LinkCallerDialog
        source={
          !linkOpen
            ? null
            : item.is_unmatched
              ? { communicationId: item.last_message_id }
              : item.lead_id
                ? { leadId: item.lead_id }
                : null
        }
        onClose={() => setLinkOpen(false)}
        onLinked={onChanged}
      />
      {(ad || hangup) && (
        <div
          className="flex min-w-0 items-center gap-1.5 border-t bg-amber-50/70 px-3 py-1 text-[11px] text-amber-900 dark:bg-amber-950/20 dark:text-amber-200"
          data-testid="thread-call-context"
        >
          {ad ? <Megaphone className="h-3 w-3 shrink-0" /> : null}
          <span className="truncate" title={hangup ?? ad ?? undefined}>
            {hangup ?? ad}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Record chip ─────────────────────────────────────────────────────────────

/**
 * "Lead" or "#854 · Edith Charron": opens the lead or case in the drawer beside
 * the dock, and closes it again. While it's open the chip fills and its caret
 * turns into an ✕, so the same spot is the way back out.
 */
function RecordChip({
  item,
  target,
  open,
  onToggle,
}: {
  item: InboxItem;
  target: RecordTarget;
  open: boolean;
  onToggle: () => void;
}) {
  const isCase = target.kind === 'funeral';
  const noun = isCase
    ? `case #${target.id}${item.deceased_name ? `, ${item.deceased_name}` : ''}`
    : 'lead';
  const action = `${open ? 'Close' : 'Open'} ${noun}`;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-haspopup="dialog"
      aria-label={action}
      title={action}
      className={cn(
        'inline-flex min-w-0 items-center gap-1 rounded-md border py-0.5 pl-1.5 pr-1 text-[11px] font-semibold leading-4',
        'transition-colors duration-[180ms] motion-reduce:transition-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        isCase
          ? open
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-primary/30 bg-background text-primary hover:bg-primary/10'
          : open
            ? 'shrink-0 border-violet-600 bg-violet-600 text-white'
            : 'shrink-0 border-transparent bg-violet-100 text-violet-700 hover:border-violet-300',
      )}
    >
      {isCase ? (
        <>
          <span className="shrink-0 font-mono font-medium">#{target.id}</span>
          {item.deceased_name && (
            <span className="min-w-0 truncate">· {item.deceased_name}</span>
          )}
        </>
      ) : (
        <span>Lead</span>
      )}
      <CaretToClose open={open} />
    </button>
  );
}

/**
 * A › that turns into an ✕: two bars that meet at a point, then lengthen and
 * cross. Plain transforms so reduced motion can simply switch it off.
 */
function CaretToClose({ open }: { open: boolean }) {
  const bar = (sign: 1 | -1): React.CSSProperties => ({
    width: open ? 10 : 6.5,
    marginLeft: open ? -5 : -3.25,
    transform: open
      ? `translate(0px, 0px) rotate(${45 * sign}deg)`
      : `translate(0.6px, ${-2.2 * sign}px) rotate(${45 * sign}deg)`,
  });
  const cls =
    'absolute left-1/2 top-1/2 -mt-[0.9px] h-[1.8px] rounded-full bg-current transition-[transform,width,margin] duration-[180ms] ease-out motion-reduce:transition-none';
  return (
    <span aria-hidden className="relative h-3 w-3 shrink-0">
      <span className={cls} style={bar(1)} />
      <span className={cls} style={bar(-1)} />
    </span>
  );
}

// ─── Owner chip ──────────────────────────────────────────────────────────────

/**
 * The conversation's case or lead owner, with the same control the case and
 * lead pages use, so either is reassigned from here. The record is read
 * through react-admin's cache, which the case/lead views share.
 */
function ConversationOwner({
  resource,
  id,
}: {
  resource: 'funerals' | 'leads';
  id: number | string;
}) {
  const { data: record } = useGetOne(resource, { id });
  if (!record) return null;
  return (
    <RecordContextProvider value={record}>
      {resource === 'leads' ? (
        <LeadOwnerControls compact />
      ) : (
        <OwnerSelector resource="funerals" noun="case" compact />
      )}
    </RecordContextProvider>
  );
}

// ─── Thread ──────────────────────────────────────────────────────────────────

export function ConversationThread({
  item,
  onBack,
  openRecord,
  onToggleRecord,
  onChanged,
  className,
}: {
  item: InboxItem;
  onBack: () => void;
  /** The record showing in the drawer beside the dock, if any. */
  openRecord: RecordTarget | null;
  onToggleRecord: (target: RecordTarget) => void;
  onChanged: () => void;
  className?: string;
}) {
  // A row headed by an email opens that email, with Reply a click away (T-231).
  const openEmailId = item.last_message_channel === 'email' ? item.last_message_id : undefined;
  return (
    <div className={cn('flex min-h-0 flex-1 flex-col bg-background', className)}>
      <ThreadHeader
        item={item}
        onBack={onBack}
        openRecord={openRecord}
        onToggleRecord={onToggleRecord}
        onChanged={onChanged}
      />
      {item.funeral_id != null ? (
        <FuneralConversation
          key={item.funeral_id}
          funeralId={item.funeral_id}
          openEmailId={openEmailId}
        />
      ) : item.is_unmatched ? (
        <UnmatchedConversation
          key={item.conversation_key}
          item={item}
          openEmailId={openEmailId}
        />
      ) : (
        <ContactConversation key={item.conversation_key} item={item} openEmailId={openEmailId} />
      )}
    </div>
  );
}
